import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Users, X, Loader2, Mail, IdCard } from 'lucide-react';

export interface BundleSubscriber {
  id: string; // doc id
  userId: string;
  customerEmail?: string | null;
  customerName?: string | null;
  stripeSubscriptionId?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  error?: string | null;
  bundleName: string;
  subscribers: BundleSubscriber[];
  onViewCustomer: (identifier: string) => void;
}

const BundleSubscribersModal: React.FC<Props> = ({ open, onClose, loading, error, bundleName, subscribers, onViewCustomer }) => {
  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
          style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.5, bounce: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            <div className="relative bg-gradient-to-r from-emerald-600 to-jamaica-green p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/15 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Active Subscribers</h2>
                    <p className="text-white/85 text-sm">{bundleName}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20 hover:text-white rounded-full h-10 w-10">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50/60">
              <div className="p-6 space-y-4">
                {loading ? (
                  <div className="flex items-center justify-center py-12 text-gray-600">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading subscribers...
                  </div>
                ) : error ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
                ) : subscribers.length === 0 ? (
                  <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-600">
                    No active subscribers found for this bundle.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {subscribers.map((sub) => (
                      <div key={sub.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                            {sub.stripeSubscriptionId && (
                              <span className="text-xs text-gray-500">{sub.stripeSubscriptionId}</span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-700">
                            <span className="font-medium truncate max-w-xs">{sub.customerName || 'Unknown Name'}</span>
                            {sub.customerEmail && (
                              <span className="inline-flex items-center gap-1 text-gray-600 truncate">
                                <Mail className="h-3.5 w-3.5" /> {sub.customerEmail}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 text-gray-600 truncate">
                              <IdCard className="h-3.5 w-3.5" /> {sub.userId}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            onClick={() => onViewCustomer(sub.customerEmail || sub.userId)}
                            className="h-9"
                          >
                            View Customer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-white border-t border-gray-100 flex items-center justify-end">
              <Button onClick={onClose} className="bg-jamaica-green hover:bg-jamaica-green/90 text-white">Close</Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BundleSubscribersModal;

